import { gql, useMutation } from "@apollo/client";
import { useContext, useState } from "react";
import { FragmentDefiner } from "../../../GlobalHooks/useReadFragment";
import { monthLongName } from "@Utils/dateFormats";
import { ConfigStateContext } from "shared-components/Contexts/Configurations/context";
import { getStratumText } from "src/utils/Functions";
import { TechnicalSheetType } from "shared-components";

export const FRAGMENT_PROJECT_INFO = new FragmentDefiner(
	"Property",
	`
    project {
		id
        percentage_sold
        percentage_finished
		ocupation
		builder
		finalization
		constructionState{
			name
		}
    }
`
);

const pojectInfoTitles = Object.freeze({
	percentageSold: "Porcentaje vendido",
	percentageFinished: "Porcentaje terminado",
	ocupation: "Ocupación",
	builder: "Desarrollador",
	finalization: "Fecha de Finalización",
	constructionState: "Estado del Proyecto",
	stratum: "Estrato",
	garages: "Parqueaderos",
	financing: "Financiación",
});

export type ProjectInfoType = {
	percentageSold: number | null;
	percentageFinished: number | null;
	ocupation: Date | null;
	builder: string;
	finalization: string;
	constructionState: string;
	stratum?: number;
	garages?: number;
	technicalSheet?: TechnicalSheetType[]; //la Technical 
};

export type ProjectInfoItem = {
	title: string;
	value: string;
	key: string;
	show: boolean
};

export interface useProjectInfoResponse {
	projectInfoList: ProjectInfoItem[];
	sendLead: (field: string) => Promise<any>;
	formCompleted: Boolean;
}

interface ContactInput {
	property_id: string;
	source: number;
	type: string;
	financing_information: boolean;
	message?: string;
	visit_type?: string;
	date?: string;
	utm_args?: UtmArgs;
	isAutoGenerated?: boolean;
}

interface UtmArgs {
	utm_content?: string | string[];
	utm_source?: string | string[];
	utm_medium?: string | string[];
	utm_campaign?: string | string[];
}

const SEND_LEAD_MUTATION = gql`
  mutation sendLead($input: LeadInput!) {
    sendLead(input: $input) {
      id
    }
  }
`;

export const useProjectInfo = (
	id: string,
	projectInfo: ProjectInfoType,
	utm = null
): useProjectInfoResponse => {
	const [formCompleted, setFormCompleted] = useState(false);
	const [sendLeadMutation] = useMutation(SEND_LEAD_MUTATION, {
		onCompleted: () => setFormCompleted(true),
	});
	const { country_code } = useContext(ConfigStateContext);
	const sendLead = async (field: string) => {
		let input: ContactInput = {
			type: "PROJECT",
			property_id: id,
			message: field,
			source: 14,
			isAutoGenerated: true,
			financing_information: false,
		};

		if (utm && (utm.utm_campaign || utm.utm_content || utm.utm_medium || utm.utm_source)) {
			input.utm_args = {};
			if (utm.utm_campaign) {
				input.utm_args.utm_campaign = Array.isArray(utm.utm_campaign) ? utm.utm_campaign[0] : utm.utm_campaign.toString();
			}
			if (utm.utm_content) {
				input.utm_args.utm_content = Array.isArray(utm.utm_content) ? utm.utm_content[0] : utm.utm_content.toString();
			}
			if (utm.utm_medium) {
				input.utm_args.utm_medium = Array.isArray(utm.utm_medium) ? utm.utm_medium[0] : utm.utm_medium.toString();
			}
			if (utm.utm_source) {
				input.utm_args.utm_source = Array.isArray(utm.utm_source) ? utm.utm_source[0] : utm.utm_source.toString();
			}
		}

		return sendLeadMutation({
			variables: {
				input,
			},
		});
	};

	const formatData = (key: string, value: any): string => {
		if (!value) return null;

		switch (key) {
			case "percentageSold":
				return `${value}%`;
			case "percentageFinished":
				return `${value}%`;
			case "ocupation":
				const date = new Date(value);
				return `${monthLongName(value)} ${date.getUTCFullYear()}`;
			default:
				return value;
		}
	};

	const showItem = (key) => {
		if (key === "constructionState") {
			return true;
		}
		return true;
	}

	const makeProjectInfoList = (info: ProjectInfoType): ProjectInfoItem[] => {
		const processEntry = (key: string, value: any, title: string, show: boolean): ProjectInfoItem => ({
			title,
			value,
			key,
			show
		});

		if ("technicalSheet" in info && country_code == "CO") {
			return Object.entries(info.technicalSheet).map(([key, value]) =>
				processEntry(key, value.value, value.text, true)
			);
		}

		return Object.entries(info).map(([key, value]) => {
			const title = pojectInfoTitles[key];
			const formattedValue = key === "stratum" ? getStratumText(value) : formatData(key, value);
			const show = showItem(key);
			return processEntry(key, formattedValue, title, show);
		});
	};

	return {
		projectInfoList: makeProjectInfoList(projectInfo),
		sendLead,
		formCompleted,
	};
};
